/**
 * Keyboard Shortcuts Configuration
 *
 * Central registry for all keyboard shortcuts in the app.
 * Used by both the shortcut handlers and the cheat sheet modal.
 */

export const SHORTCUT_SCOPES = {
  GLOBAL: 'global',
  LIST: 'list',
  READER: 'reader',
  FLASHCARD: 'flashcard',
  EDITING: 'editing',
};

export const SHORTCUTS = {
  // ===== Global Shortcuts =====
  SEARCH: {
    keys: ['mod+k'],
    scope: SHORTCUT_SCOPES.GLOBAL,
    i18nKey: 'shortcuts.search',
    displayKeys: ['⌘/Ctrl', '+', 'K'],
  },
  SHOW_HELP: {
    keys: ['h'],
    scope: SHORTCUT_SCOPES.GLOBAL,
    i18nKey: 'shortcuts.showHelp',
    displayKeys: ['H'],
  },
  GO_TO_ALL: {
    keys: ['shift+1', '!'],
    scope: SHORTCUT_SCOPES.GLOBAL,
    i18nKey: 'shortcuts.goToAll',
    displayKeys: ['Shift+1'],
  },
  GO_TO_INBOX: {
    keys: ['shift+2', '@'],
    scope: SHORTCUT_SCOPES.GLOBAL,
    i18nKey: 'shortcuts.goToInbox',
    displayKeys: ['Shift+2'],
  },

  // ===== List Shortcuts =====
  SELECT_PREV_ARTICLE: {
    keys: ['up', 'k'],
    scope: SHORTCUT_SCOPES.LIST,
    i18nKey: 'shortcuts.selectPrevArticle',
    displayKeys: ['↑', 'K'],
  },
  SELECT_NEXT_ARTICLE: {
    keys: ['down', 'j'],
    scope: SHORTCUT_SCOPES.LIST,
    i18nKey: 'shortcuts.selectNextArticle',
    displayKeys: ['↓', 'J'],
  },
  OPEN_ARTICLE: {
    keys: ['enter'],
    scope: SHORTCUT_SCOPES.LIST,
    i18nKey: 'shortcuts.openArticle',
    displayKeys: ['Enter'],
  },
  FOCUS_ADD_INPUT: {
    keys: ['a'],
    scope: SHORTCUT_SCOPES.LIST,
    i18nKey: 'shortcuts.focusAddInput',
    displayKeys: ['A'],
  },

  // ===== Reader Shortcuts =====
  HOVER_PREV_BLOCK: {
    keys: ['up', 'k'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.hoverPrevBlock',
    displayKeys: ['↑', 'K'],
  },
  HOVER_NEXT_BLOCK: {
    keys: ['down', 'j'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.hoverNextBlock',
    displayKeys: ['↓', 'J'],
  },
  MARK_BLOCK_READ: {
    keys: ['enter'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.markBlockRead',
    displayKeys: ['Enter'],
  },
  TOGGLE_AI_PANEL: {
    keys: ['g'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.toggleAIPanel',
    displayKeys: ['G'],
  },
  TOGGLE_FOCUS_GUIDE: {
    keys: ['f'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.toggleFocusGuide',
    displayKeys: ['F'],
  },
  BACK_TO_LIST: {
    keys: ['escape'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.backToList',
    displayKeys: ['Esc'],
  },
  QUICK_SELECT: {
    keys: ['shift+click'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.quickSelect',
    displayKeys: ['Shift', '+', 'Click'],
  },
  MARK_READ_JUMP: {
    keys: ['m'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.markReadJump',
    displayKeys: ['M'],
  },
  HIGHLIGHT_BLOCK: {
    keys: ['b'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.highlightBlock',
    displayKeys: ['B'],
  },
  NOTE_BLOCK: {
    keys: ['n'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.noteBlock',
    displayKeys: ['N'],
  },
  PREV_COMMENT: {
    keys: ['shift+up'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.prevComment',
    displayKeys: ['Shift+↑'],
  },
  NEXT_COMMENT: {
    keys: ['shift+down'],
    scope: SHORTCUT_SCOPES.READER,
    i18nKey: 'shortcuts.nextComment',
    displayKeys: ['Shift+↓'],
  },

  // ===== Flashcard Shortcuts =====
  FLIP_CARD: {
    keys: ['space'],
    scope: SHORTCUT_SCOPES.FLASHCARD,
    i18nKey: 'shortcuts.flipCard',
    displayKeys: ['Space'],
  },
  RATE_AGAIN: {
    keys: ['1'],
    scope: SHORTCUT_SCOPES.FLASHCARD,
    i18nKey: 'shortcuts.rateAgain',
    displayKeys: ['1'],
  },
  RATE_HARD: {
    keys: ['2'],
    scope: SHORTCUT_SCOPES.FLASHCARD,
    i18nKey: 'shortcuts.rateHard',
    displayKeys: ['2'],
  },
  RATE_GOOD: {
    keys: ['3'],
    scope: SHORTCUT_SCOPES.FLASHCARD,
    i18nKey: 'shortcuts.rateGood',
    displayKeys: ['3'],
  },
  RATE_EASY: {
    keys: ['4'],
    scope: SHORTCUT_SCOPES.FLASHCARD,
    i18nKey: 'shortcuts.rateEasy',
    displayKeys: ['4'],
  },

  // ===== Editing Shortcuts (Comment Input, etc.) =====
  SUBMIT_COMMENT: {
    keys: ['mod+enter'],
    scope: SHORTCUT_SCOPES.EDITING,
    i18nKey: 'shortcuts.submitComment',
    displayKeys: ['⌘/Ctrl', '+', 'Enter'],
  },
  CANCEL_EDIT: {
    keys: ['escape'],
    scope: SHORTCUT_SCOPES.EDITING,
    i18nKey: 'shortcuts.cancelEdit',
    displayKeys: ['Esc'],
  },
};

// Group shortcuts by scope for the modal display
export const SHORTCUT_GROUPS = [
  {
    titleKey: 'shortcuts.groups.navigation',
    shortcuts: [
      SHORTCUTS.SEARCH,
      SHORTCUTS.SHOW_HELP,
      SHORTCUTS.GO_TO_ALL,
      SHORTCUTS.GO_TO_INBOX,
    ],
  },
  {
    titleKey: 'shortcuts.groups.articleList',
    shortcuts: [
      SHORTCUTS.SELECT_PREV_ARTICLE,
      SHORTCUTS.SELECT_NEXT_ARTICLE,
      SHORTCUTS.OPEN_ARTICLE,
      SHORTCUTS.FOCUS_ADD_INPUT,
    ],
  },
  {
    titleKey: 'shortcuts.groups.reader',
    shortcuts: [
      SHORTCUTS.HOVER_PREV_BLOCK,
      SHORTCUTS.HOVER_NEXT_BLOCK,
      SHORTCUTS.MARK_BLOCK_READ,
      SHORTCUTS.MARK_READ_JUMP,
      SHORTCUTS.HIGHLIGHT_BLOCK,
      SHORTCUTS.NOTE_BLOCK,
      SHORTCUTS.PREV_COMMENT,
      SHORTCUTS.NEXT_COMMENT,
      SHORTCUTS.QUICK_SELECT,
      SHORTCUTS.TOGGLE_AI_PANEL,
      SHORTCUTS.TOGGLE_FOCUS_GUIDE,
      SHORTCUTS.BACK_TO_LIST,
    ],
  },
  {
    titleKey: 'shortcuts.groups.flashcard',
    shortcuts: [
      SHORTCUTS.FLIP_CARD,
      SHORTCUTS.RATE_AGAIN,
      SHORTCUTS.RATE_HARD,
      SHORTCUTS.RATE_GOOD,
      SHORTCUTS.RATE_EASY,
    ],
  },
  {
    titleKey: 'shortcuts.groups.editing',
    shortcuts: [
      SHORTCUTS.SUBMIT_COMMENT,
      SHORTCUTS.CANCEL_EDIT,
    ],
  },
];
